import {
    CallExpressionShape,
    type ObjectShape,
    Plugin,
    type Record as SdkRecord,
    TemplateValueShape,
} from '@servicenow/sdk-build-core'
import type { Shape } from '@servicenow/sdk-build-core'
import {
    ACTIVITY_DEFAULTS,
    ACTIVITY_TABLE,
    SYS_ELEMENT_MAPPING_TABLE,
    SYS_VARIABLE_VALUE_TABLE,
    encodeKeywordsToken,
} from '../constants/playbook-plugin-constants'
import { DECISION_DEF_SYS_ID } from '../constants/activity-ootb-maps'
import { ActivityShape, getFactoryCallConfig } from '../utils/playbook-shapes'
import { ActivityDiagnosticsPlugin } from './activity-diagnostics-plugin'
import { stripHtmlTags } from '../utils/label-utils'
import { resolveStartRuleToDatabase } from '../utils/start-rule-resolver'
import { slugifyString } from '../../utils/slugify.js'
import { generateId } from '@servicenow/sdk-build-plugins'
import { activityRecordToShape } from '../transforms/activity-config-transform'
import { isPlaybookUnsupported } from '../utils/transform-utils'
import { timerAttributesToRecord } from '../transforms/timer-attributes-transform'
import { type PillContext, resolvePlaybookPill } from '../utils/playbook-pill-resolver'
import { normalizeTemplateValue } from '../utils/template-value-normalizer'
import { containsDataPill } from '../utils/playbook-shapes'
import { STANDARD_PILL_REGEX } from '../utils/playbook-pill-transformer'

/**
 * Extract the sys_id from a variable entry shape.
 * Entries are Column calls like `StringColumn({ sysId: '...', ... })` (CallExpressionShape)
 * or plain objects with a `sysId` property (ObjectShape).
 */
function extractVariableSysId(shape: Shape): string | undefined {
    const obj = shape.if(CallExpressionShape)?.getArgument(0)?.ifObject() ?? shape.ifObject()
    return obj?.get('sysId', false)?.ifString()?.getValue()
}

/**
 * Extract the column-level `defaultValue` from a variable entry shape, if present.
 * Entries are Column calls like `StringColumn({ sysId: '...', defaultValue: '4' })`
 * (CallExpressionShape) or plain objects. Returns undefined when no default is declared.
 */

// biome-ignore lint/suspicious/noExplicitAny: Default values may be any type matching the backing column type
function extractColumnDefaultValue(shape: Shape): any {
    const obj = shape.if(CallExpressionShape)?.getArgument(0)?.ifObject() ?? shape.ifObject()
    const value = obj?.get('defaultValue', false)
    return value?.getValue()
}

/**
 * Resolved activity definition config with pre-extracted input and experience property maps.
 */
interface ResolvedActivityDef {
    activityDefSysId: Now.Internal.ExplicitKey | string | number
    backingInputs: ObjectShape | undefined
    backingExperienceProperties: ObjectShape | undefined
    /** sys_id of the backing flow or action (for sys_element_mapping table name) */
    backingSysId: string | undefined
    /** Whether the backing automation is a flow or action (determines sys_element_mapping table prefix) */
    backingType: 'flow' | 'action' | undefined
    /** sys_id of the activity type (for sys_element_mapping table name) */
    activityTypeSysId: string | undefined
    /** Default values for inputs keyed by fluent name. Applied when Activity() does not provide a value. */
    defaultInputs: Record<string, any>
    /** Default values for experience properties keyed by fluent name. Applied when Activity() does not provide a value. */
    defaultExperienceProperties: Record<string, any>
}

/**
 * Resolve the activity definition to extract its sys_id, input variable map, and typeVal map.
 *
 * Resolves through the shape system by following the IdentifierShape for the definition,
 * then traverses the flow/action and activityType references to extract their configs.
 * Works for both OOTB definitions (which reference .now.ts backing files) and custom
 * user-defined definitions.
 */
function resolveActivityDef(activityDefShape: Shape): ResolvedActivityDef | undefined {
    const activityDefConfig = getFactoryCallConfig(activityDefShape)
    if (!activityDefConfig) {
        return undefined
    }

    const activityDefSysId = activityDefConfig.get('$id', false)?.ifString()?.getValue()
    if (!activityDefSysId) {
        return undefined
    }

    const flowShape = activityDefConfig.get('flow')
    const actionShape = activityDefConfig.get('action')
    const backingShape = flowShape?.ifDefined() ?? actionShape
    const backingType: 'flow' | 'action' | undefined = flowShape?.ifDefined()
        ? 'flow'
        : actionShape?.ifDefined()
          ? 'action'
          : undefined
    const backingConfig = getFactoryCallConfig(backingShape)
    const activityTypeConfig = getFactoryCallConfig(activityDefConfig.get('activityType'))

    return {
        activityDefSysId,
        backingInputs: backingConfig?.get('inputs', false)?.ifObject(),
        backingExperienceProperties: activityTypeConfig?.get('experienceProperties', false)?.ifObject(),
        backingSysId: backingConfig?.get('$id', false)?.ifString()?.getValue(),
        backingType,
        activityTypeSysId: activityTypeConfig?.get('$id', false)?.ifString()?.getValue(),
        defaultInputs: activityDefConfig.get('defaultInputs', false)?.ifObject()?.getValue() ?? {},
        defaultExperienceProperties:
            activityDefConfig.get('defaultExperienceProperties', false)?.ifObject()?.getValue() ?? {},
    }
}

export const ActivityPlugin = Plugin.create({
    name: 'ActivityPlugin',
    records: {
        [ACTIVITY_TABLE]: {
            relationships: {
                [SYS_VARIABLE_VALUE_TABLE]: {
                    via: 'document_key',
                    descendant: true,
                },
                [SYS_ELEMENT_MAPPING_TABLE]: {
                    via: 'id',
                    descendant: true,
                },
            },
            toShape(record, { database }, guidIdToVarName?: Map<string, string>) {
                if (isPlaybookUnsupported(record, database)) {
                    return { success: false }
                }
                return activityRecordToShape(record, database, guidIdToVarName)
            },
        },
        [SYS_VARIABLE_VALUE_TABLE]: {
            coalesce: ['document_key', 'variable'],
        },
        [SYS_ELEMENT_MAPPING_TABLE]: {
            coalesce: ['field', 'table', 'id'],
        },
    },
    shapes: [
        {
            shape: ActivityShape,
            fileTypes: ['fluent'],
            async toRecord(activityShape, { factory, transform, diagnostics }) {
                const config = activityShape.getConfig()

                // Name resolution: the parent collection injects `uniqueName` — the de-duped
                // authoritative name (it equals the explicit name when there's no collision, and a
                // suffixed variant when it had to be de-duped). Prefer it; fall back to the explicit
                // name or a slugified label only when this activity is processed outside a collection.
                const injectedName = config.get('uniqueName')?.ifString()?.getValue()
                let computedName = injectedName
                if (!computedName) {
                    const explicitName = config.get('name')?.ifString()?.getValue()
                    const labelVal = config.get('label')?.ifString()?.getValue()
                    // Strip HTML before slugifying so the fallback name matches the stripped
                    // label that ActivityPlugin actually persists (see the toRecord block below).
                    computedName = explicitName ?? (labelVal ? slugifyString(stripHtmlTags(labelVal)) : '')
                }

                // Resolve the activity definition to extract the def sys_id, input map, and typeVal map.
                const resolvedActivityDef = resolveActivityDef(activityShape.getActivityDefinition())
                if (!resolvedActivityDef) {
                    return { success: false }
                }
                const {
                    activityDefSysId,
                    backingInputs,
                    backingExperienceProperties,
                    backingSysId,
                    backingType,
                    activityTypeSysId,
                    defaultInputs,
                    defaultExperienceProperties,
                } = resolvedActivityDef
                const isDecision = String(activityDefSysId) === DECISION_DEF_SYS_ID

                // Run activity diagnostics (dependent field validation for all
                // activities, plus Decision-specific checks for Decision activities).
                await transform.toRecord(activityShape, ActivityDiagnosticsPlugin)

                const playbookBodyParamsNames = activityShape.getPlaybookBodyParamsNames()
                const conditionPillContext: PillContext = {
                    format: 'condition',
                    factory,
                    diagnostics,
                    playbookBodyParamsNames,
                    allowedSources: {
                        paramsInputs: true,
                        paramsState: false,
                        paramsParentRecord: true,
                        previousActivityOutputs: true,
                        currentActivityLabelDesc: false,
                        automationPlan: false,
                    },
                }

                // Resolve conditionToRun pills before record creation (config.transform is synchronous)
                const conditionToRunShape = config.get('conditionToRun')
                const resolvedConditionToRun =
                    conditionToRunShape && !conditionToRunShape.isUndefined()
                        ? await resolvePlaybookPill(conditionToRunShape, conditionPillContext)
                        : undefined

                const activityRecord = await factory.createRecord({
                    source: activityShape,
                    table: ACTIVITY_TABLE,
                    explicitId: config.get('$id'),
                    properties: config.transform(({ $ }) => ({
                        activity_definition: $.val(activityDefSysId),
                        // Mirrors platform BR "Strip HTML from label": HTML is not supported on
                        // sys_pd_activity.label and the BR silently strips it on insert/update.
                        // ActivityDiagnosticsPlugin emits a warning when this strip applies.
                        label: $.map((label) => {
                            const str = label.ifString()?.getValue()
                            return str !== undefined ? stripHtmlTags(str) : label
                        }),
                        name: $.val(computedName),
                        order: $,
                        active: $.def(ACTIVITY_DEFAULTS.ACTIVE),
                        description: $,
                        start_rule_name: $.from('startRule').map((startRule) => resolveStartRuleToDatabase(startRule)),
                        starts_after_activities: $.from('startsAfterActivities'),
                        starts_after_branches: $.from('startsAfterBranches'),
                        starts_after_lanes: $.from('startsAfterLanes'),
                        condition_to_run: resolvedConditionToRun
                            ? $.val(resolvedConditionToRun)
                            : $.from('conditionToRun'),
                        restart_rule: $.from('restartRule'),
                        lane: $.def(''),
                        process_definition: $.from('processDefinition'),
                        // Preserve AI-agent fields on round-trip
                        enable_ai_agent: $.from('enableAiAgent'),
                        ai_agent_objective: $.from('aiAgentObjective'),
                        ai_agent_obj_additional_detail: $.from('aiAgentObjAdditionalDetail'),
                        ai_agent_execution_mode: $.from('aiAgentExecutionMode'),
                        ai_agent_run_with_roles: $.from('aiAgentRunWithRoles'),
                        ai_agent_run_as: $.from('aiAgentRunAs').def(ACTIVITY_DEFAULTS.AI_AGENT_RUN_AS),
                        ai_agent_user: $.from('aiAgentUser'),
                        conversational_agents: $.from('conversationalAgents'),
                        launch_agent_when_activity_start: $.from('launchAgentWhenActivityStart'),
                    })),
                })

                const relatedRecords: SdkRecord[] = []
                const activitySysId = activityRecord.getId().getValue()

                // Create timer attributes record if startWithDelay is present.
                // Activities support the same three duration types as lanes (explicit, relative, percentage)
                // and accept dataPill() references for the pill-capable fields.
                const startWithDelayShape = config.get('startWithDelay')
                if (startWithDelayShape && !startWithDelayShape.isUndefined()) {
                    const timerPillContext: PillContext = {
                        format: 'standard',
                        factory,
                        diagnostics,
                        currentActivityId: activityRecord.getId(),
                        playbookBodyParamsNames,
                        allowedSources: {
                            paramsInputs: true,
                            paramsState: true,
                            paramsParentRecord: true,
                            previousActivityOutputs: true,
                            currentActivityLabelDesc: false,
                            automationPlan: false,
                        },
                    }
                    const processDefinition = config.get('processDefinition')?.ifString()?.getValue() ?? ''
                    const timerRecord = await timerAttributesToRecord(
                        startWithDelayShape.asObject(),
                        activityRecord,
                        processDefinition,
                        'sys_pd_activity',
                        factory,
                        diagnostics,
                        timerPillContext
                    )
                    if (timerRecord) {
                        relatedRecords.push(timerRecord)
                    }
                }

                const inputPillContext: PillContext = {
                    format: 'standard',
                    factory,
                    diagnostics,
                    currentActivityId: activityRecord.getId(),
                    playbookBodyParamsNames,
                    allowedSources: {
                        paramsInputs: true,
                        paramsState: true,
                        paramsParentRecord: true,
                        previousActivityOutputs: true,
                        currentActivityLabelDesc: true,
                        automationPlan: false,
                    },
                }
                const experiencePropertyPillContext: PillContext = {
                    format: 'standard',
                    factory,
                    diagnostics,
                    currentActivityId: activityRecord.getId(),
                    playbookBodyParamsNames,
                    allowedSources: {
                        paramsInputs: true,
                        paramsState: true,
                        paramsParentRecord: true,
                        previousActivityOutputs: true,
                        currentActivityLabelDesc: true,
                        automationPlan: true,
                    },
                }

                // Generate sys_variable_value and sys_element_mapping records for inputs.
                // The platform stores pill mappings in sys_element_mapping and literal
                // values in sys_variable_value. We always create both records so that
                // switching between pill ↔ literal across builds never leaves stale data:
                //   pill:    sys_variable_value.value = ''   , sys_element_mapping.value = pill
                //   literal: sys_variable_value.value = value, sys_element_mapping.value = ''
                const inputs = activityShape.getInputs()
                if (backingInputs && inputs) {
                    for (const fluentName of inputs.keys()) {
                        // Decision branches are handled separately below
                        if (isDecision && fluentName === 'branches') {
                            continue
                        }
                        const varEntryShape = backingInputs.get(fluentName, false)
                        if (!varEntryShape) {
                            continue
                        }
                        const sysId = extractVariableSysId(varEntryShape)
                        if (!sysId) {
                            continue
                        }
                        const rawInputShape = inputs.get(fluentName)!
                        const inputShape =
                            rawInputShape instanceof TemplateValueShape
                                ? normalizeTemplateValue(rawInputShape)
                                : rawInputShape
                        const isPill = containsDataPill(inputShape)
                        const resolvedValue = await resolvePlaybookPill(inputShape, inputPillContext)
                        relatedRecords.push(
                            await factory.createRecord({
                                source: inputShape,
                                table: SYS_VARIABLE_VALUE_TABLE,
                                properties: {
                                    document: ACTIVITY_TABLE,
                                    document_key: activitySysId,
                                    variable: sysId,
                                    value: isPill ? '' : resolvedValue,
                                },
                            })
                        )
                        if (backingSysId) {
                            const inputTablePrefix =
                                backingType === 'action' ? 'sys_hub_action_input' : 'sys_hub_flow_input'
                            relatedRecords.push(
                                await factory.createRecord({
                                    source: inputShape,
                                    table: SYS_ELEMENT_MAPPING_TABLE,
                                    properties: {
                                        field: fluentName,
                                        id: activitySysId,
                                        table: `var__m_${inputTablePrefix}_${backingSysId}`,
                                        value: isPill ? resolvedValue : '',
                                    },
                                })
                            )
                        }
                    }
                }

                // Apply default input values for inputs the user did not provide in Activity().
                // Same dual-record pattern: pills go in sys_element_mapping, literals in sys_variable_value.
                // An empty counterpart record is always created to avoid stale data across builds.
                if (backingInputs) {
                    const userProvidedInputs = inputs ? new Set(inputs.keys()) : new Set<string>()
                    for (const [fluentName, defaultValue] of Object.entries(defaultInputs)) {
                        if (userProvidedInputs.has(fluentName)) {
                            continue
                        }
                        const varEntryShape = backingInputs.get(fluentName, false)
                        if (!varEntryShape) {
                            continue
                        }
                        const sysId = extractVariableSysId(varEntryShape)
                        if (!sysId) {
                            continue
                        }
                        const rawValue = String(defaultValue)
                        const isPill = [...rawValue.matchAll(STANDARD_PILL_REGEX)].length > 0
                        // Resolve {{act.act_instance. to the current activity's sys_id
                        const stringValue = isPill
                            ? rawValue.replace(/\{\{act\.act_instance\./g, `{{act.${activitySysId}.`)
                            : rawValue
                        relatedRecords.push(
                            await factory.createRecord({
                                source: activityShape,
                                table: SYS_VARIABLE_VALUE_TABLE,
                                properties: {
                                    document: ACTIVITY_TABLE,
                                    document_key: activitySysId,
                                    variable: sysId,
                                    value: isPill ? '' : stringValue,
                                },
                            })
                        )
                        if (backingSysId) {
                            const inputTablePrefix =
                                backingType === 'action' ? 'sys_hub_action_input' : 'sys_hub_flow_input'
                            relatedRecords.push(
                                await factory.createRecord({
                                    source: activityShape,
                                    table: SYS_ELEMENT_MAPPING_TABLE,
                                    properties: {
                                        field: fluentName,
                                        id: activitySysId,
                                        table: `var__m_${inputTablePrefix}_${backingSysId}`,
                                        value: isPill ? stringValue : '',
                                    },
                                })
                            )
                        }
                    }
                }

                // Handle Decision activity's branches input specially.
                // Branches need JSON serialization rather than direct value mapping.
                if (isDecision && backingInputs) {
                    const branchesShape = inputs?.get('branches')?.ifArray()
                    const branchesEntryShape = backingInputs.get('branches', false)
                    const branchesSysId = branchesEntryShape ? extractVariableSysId(branchesEntryShape) : undefined
                    if (branchesShape && branchesSysId) {
                        const branchesJson: Array<{
                            id: string
                            label: string
                            condition: string
                            order: number
                            activitiesCount: number
                        }> = []
                        for (const [index, elem] of branchesShape.getElements().entries()) {
                            const branch = elem.ifObject()
                            const rawId = branch?.get('id')?.ifString()?.getValue() ?? ''
                            const rawLabel = branch?.get('label')?.ifString()?.getValue() ?? ''
                            const label = rawId === 'else' ? 'Else' : rawLabel
                            const id =
                                rawId === 'else'
                                    ? 'ELSE'
                                    : generateId(activitySysId, 'branch', slugifyString(label) || rawId)
                            const conditionShape = branch?.get('condition')
                            let condition = ''
                            if (conditionShape) {
                                const resolved = await resolvePlaybookPill(conditionShape, conditionPillContext)
                                const rawCondition =
                                    resolved.ifString()?.getValue() ?? resolved.getValue()?.toString() ?? ''
                                // Replace `keywords` only in field position (start of segment),
                                // not when it appears as a value (e.g., field=keywords)
                                condition = encodeKeywordsToken(rawCondition)
                            }
                            branchesJson.push({ id, label, condition, order: index + 1, activitiesCount: 0 })
                        }
                        relatedRecords.push(
                            await factory.createRecord({
                                source: branchesShape,
                                table: SYS_VARIABLE_VALUE_TABLE,
                                properties: {
                                    document: ACTIVITY_TABLE,
                                    document_key: activitySysId,
                                    variable: branchesSysId,
                                    value: JSON.stringify(branchesJson),
                                },
                            })
                        )
                    }

                    // Default type to 'match_first' when not explicitly provided
                    const hasType = inputs && [...inputs.keys()].includes('type')
                    if (!hasType) {
                        const typeEntryShape = backingInputs.get('type', false)
                        const typeSysId = typeEntryShape ? extractVariableSysId(typeEntryShape) : undefined
                        if (typeSysId) {
                            relatedRecords.push(
                                await factory.createRecord({
                                    source: activityShape,
                                    table: SYS_VARIABLE_VALUE_TABLE,
                                    properties: {
                                        document: ACTIVITY_TABLE,
                                        document_key: activitySysId,
                                        variable: typeSysId,
                                        value: 'match_first',
                                    },
                                })
                            )
                        }
                    }
                }

                // Generate sys_variable_value and sys_element_mapping records for experience properties
                const experienceProperties = activityShape.getExperienceProperties()
                if (backingExperienceProperties && experienceProperties) {
                    for (const fluentName of experienceProperties.keys()) {
                        const varEntryShape = backingExperienceProperties.get(fluentName, false)
                        if (!varEntryShape) {
                            continue
                        }
                        const sysId = extractVariableSysId(varEntryShape)
                        if (!sysId) {
                            continue
                        }
                        const rawExpPropShape = experienceProperties.get(fluentName)!
                        const expPropShape =
                            rawExpPropShape instanceof TemplateValueShape
                                ? normalizeTemplateValue(rawExpPropShape)
                                : rawExpPropShape
                        const isPill = containsDataPill(expPropShape)
                        const resolvedValue = await resolvePlaybookPill(expPropShape, experiencePropertyPillContext)
                        relatedRecords.push(
                            await factory.createRecord({
                                source: expPropShape,
                                table: SYS_VARIABLE_VALUE_TABLE,
                                properties: {
                                    document: ACTIVITY_TABLE,
                                    document_key: activitySysId,
                                    variable: sysId,
                                    value: isPill ? '' : resolvedValue,
                                },
                            })
                        )
                        if (activityTypeSysId) {
                            relatedRecords.push(
                                await factory.createRecord({
                                    source: expPropShape,
                                    table: SYS_ELEMENT_MAPPING_TABLE,
                                    properties: {
                                        field: fluentName,
                                        id: activitySysId,
                                        table: `var__m_sys_pd_activity_type_prop_${activityTypeSysId}`,
                                        value: isPill ? resolvedValue : '',
                                    },
                                })
                            )
                        }
                    }
                }

                // Apply default experience property values for properties the user did not provide.
                // Precedence: the activity definition's defaultExperienceProperties wins; otherwise
                // fall back to a column-level defaultValue declared on the activity type.
                if (backingExperienceProperties) {
                    const userProvidedExpProps = experienceProperties
                        ? new Set(experienceProperties.keys())
                        : new Set<string>()
                    for (const fluentName of backingExperienceProperties.keys()) {
                        if (userProvidedExpProps.has(fluentName)) {
                            continue
                        }
                        const varEntryShape = backingExperienceProperties.get(fluentName, false)
                        if (!varEntryShape) {
                            continue
                        }
                        const defaultValue =
                            fluentName in defaultExperienceProperties
                                ? defaultExperienceProperties[fluentName]
                                : extractColumnDefaultValue(varEntryShape)
                        if (defaultValue === undefined) {
                            continue
                        }
                        const sysId = extractVariableSysId(varEntryShape)
                        if (!sysId) {
                            continue
                        }
                        const rawValue = String(defaultValue)
                        const isPill = [...rawValue.matchAll(STANDARD_PILL_REGEX)].length > 0
                        // Resolve {{act.act_instance. to the current activity's sys_id
                        const stringValue = isPill
                            ? rawValue.replace(/\{\{act\.act_instance\./g, `{{act.${activitySysId}.`)
                            : rawValue
                        relatedRecords.push(
                            await factory.createRecord({
                                source: activityShape,
                                table: SYS_VARIABLE_VALUE_TABLE,
                                properties: {
                                    document: ACTIVITY_TABLE,
                                    document_key: activitySysId,
                                    variable: sysId,
                                    value: isPill ? '' : stringValue,
                                },
                            })
                        )
                        if (activityTypeSysId) {
                            relatedRecords.push(
                                await factory.createRecord({
                                    source: activityShape,
                                    table: SYS_ELEMENT_MAPPING_TABLE,
                                    properties: {
                                        field: fluentName,
                                        id: activitySysId,
                                        table: `var__m_sys_pd_activity_type_prop_${activityTypeSysId}`,
                                        value: isPill ? stringValue : '',
                                    },
                                })
                            )
                        }
                    }
                }

                return {
                    success: true,
                    value: activityRecord.with(...relatedRecords),
                }
            },
        },
    ],
    docs: [],
})
